import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, ExternalLink, X, Code2, Eye, Copy, Check, Maximize2, Minimize2, RefreshCw, Edit3, Sparkles, Loader2, Star, Bookmark, Trash2, Plus, Search } from 'lucide-react';
import { toast } from 'sonner';
import Editor from '@monaco-editor/react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Detect code type from language hint or content
function detectCodeType(code, lang) {
  const lowerLang = (lang || '').toLowerCase();
  
  if (lowerLang === 'html' || code.includes('<!DOCTYPE') || code.includes('<html')) return 'html';
  if (lowerLang === 'jsx' || lowerLang === 'tsx' || code.includes('import React') || code.includes('useState')) return 'react';
  if (lowerLang === 'css') return 'css';
  if (lowerLang === 'javascript' || lowerLang === 'js') return 'javascript';
  if (lowerLang === 'python' || lowerLang === 'py') return 'python';
  if (code.includes('<style>') || code.includes('<script>')) return 'html';
  
  return 'unknown';
}

// Extract all code blocks from messages
export function extractCodeBlocks(messages) {
  if (!messages || !Array.isArray(messages)) return [];
  
  const blocks = [];
  const regex = /```(\w+)?\n([\s\S]*?)```/g;
  
  for (let i = 0; i < messages.length; i++) {
    if (messages[i].role !== 'assistant') continue;
    const content = messages[i].content;
    let match;
    let blockIndex = 0;
    
    while ((match = regex.exec(content)) !== null) {
      const code = match[2].trim();
      if (code.length > 20) {
        const lang = match[1] || '';
        const type = detectCodeType(code, lang);
        blocks.push({
          id: `block-${i}-${blockIndex}`,
          language: lang,
          type: type,
          code: code,
          messageIndex: i
        });
        blockIndex++;
      }
    }
  }
  
  return blocks;
}

// Check if code can be previewed
export function canPreviewCode(blocks) {
  return blocks.some(b => ['html', 'react', 'javascript', 'css'].includes(b.type));
}

// Generate CodeSandbox URL with edited code
function generateCodeSandboxUrl(blocks, editedCode = {}) {
  const getCode = (block) => editedCode[block.id] || block.code;
  
  const htmlBlocks = blocks.filter(b => b.type === 'html');
  const cssBlocks = blocks.filter(b => b.type === 'css');
  const jsBlocks = blocks.filter(b => b.type === 'javascript');
  const reactBlocks = blocks.filter(b => b.type === 'react');
  
  if (reactBlocks.length > 0) {
    const files = {
      'package.json': {
        content: JSON.stringify({
          dependencies: { react: "18.2.0", "react-dom": "18.2.0" }
        })
      },
      'index.js': {
        content: `import React from 'react';\nimport { createRoot } from 'react-dom/client';\nimport App from './App';\nconst root = createRoot(document.getElementById('root'));\nroot.render(<App />);`
      },
      'App.js': { content: getCode(reactBlocks[0]) }
    };
    
    if (cssBlocks.length > 0) {
      files['styles.css'] = { content: cssBlocks.map(b => getCode(b)).join('\n') };
      files['App.js'].content = `import './styles.css';\n${files['App.js'].content}`;
    }
    
    const params = new URLSearchParams({ parameters: btoa(JSON.stringify({ files })) });
    return `https://codesandbox.io/api/v1/sandboxes/define?${params.toString()}`;
  }
  
  const html = htmlBlocks.map(b => getCode(b)).join('\n') || '<!DOCTYPE html>\n<html>\n<head></head>\n<body>\n<div id="app"></div>\n</body>\n</html>';
  const css = cssBlocks.map(b => getCode(b)).join('\n');
  const js = jsBlocks.map(b => getCode(b)).join('\n');
  
  const files = {
    'index.html': { content: html },
    'styles.css': { content: css || '/* Styles */' },
    'index.js': { content: js || '// JavaScript' }
  };
  
  const params = new URLSearchParams({ parameters: btoa(JSON.stringify({ files })) });
  return `https://codesandbox.io/api/v1/sandboxes/define?${params.toString()}`;
}

// Generate preview HTML with edited code
function generatePreviewHtml(blocks, editedCode = {}) {
  const getCode = (block) => editedCode[block.id] || block.code;
  
  const htmlBlocks = blocks.filter(b => b.type === 'html');
  const cssBlocks = blocks.filter(b => b.type === 'css');
  const jsBlocks = blocks.filter(b => b.type === 'javascript');
  
  let css = cssBlocks.map(b => getCode(b)).join('\n');
  let js = jsBlocks.map(b => getCode(b)).join('\n');
  
  if (htmlBlocks.length > 0) {
    let html = getCode(htmlBlocks[0]);
    if (css && !html.includes('<style>')) {
      html = html.replace('</head>', `<style>${css}</style></head>`);
    }
    if (js && !html.includes('<script>')) {
      html = html.replace('</body>', `<script>${js}</script></body>`);
    }
    return html;
  }
  
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <style>* { margin: 0; padding: 0; box-sizing: border-box; } body { font-family: system-ui, sans-serif; } ${css}</style>
</head>
<body>
  <div id="app"></div>
  <script>${js}</script>
</body>
</html>`;
}

// Get Monaco language from code type
function getMonacoLanguage(type, lang) {
  if (type === 'html') return 'html';
  if (type === 'css') return 'css';
  if (type === 'javascript') return 'javascript';
  if (type === 'react' || lang === 'jsx' || lang === 'tsx') return 'javascript';
  if (type === 'python') return 'python';
  return 'plaintext';
}

export function CodePreviewModal({ isOpen, onClose, messages, onSaveCode }) {
  const [activeTab, setActiveTab] = useState('preview');
  const [selectedBlock, setSelectedBlock] = useState(0);
  const [copied, setCopied] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);
  const [editedCode, setEditedCode] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [suggestion, setSuggestion] = useState('');
  const [loadingSuggestion, setLoadingSuggestion] = useState(false);
  const [aiEnabled, setAiEnabled] = useState(true);
  const [suggestionHistory, setSuggestionHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [snippets, setSnippets] = useState([]);
  const [showSnippets, setShowSnippets] = useState(false);
  const [loadingSnippets, setLoadingSnippets] = useState(false);
  const [snippetSearch, setSnippetSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showSaveSnippet, setShowSaveSnippet] = useState(false);
  const [newSnippetName, setNewSnippetName] = useState('');
  const [newSnippetCategory, setNewSnippetCategory] = useState('general');
  const iframeRef = useRef(null);
  const editorRef = useRef(null);
  const suggestionTimeoutRef = useRef(null);
  
  const CATEGORIES = ['general', 'components', 'functions', 'styles', 'utils', 'templates'];
  
  const originalBlocks = extractCodeBlocks(messages);
  const blocks = originalBlocks.map(b => ({
    ...b,
    code: editedCode[b.id] || b.code
  }));
  const previewable = blocks.filter(b => ['html', 'javascript', 'css'].includes(b.type));
  const hasReact = blocks.some(b => b.type === 'react');
  
  // Fetch snippets
  const fetchSnippets = useCallback(async () => {
    setLoadingSnippets(true);
    try {
      const token = localStorage.getItem('token');
      let url = `${API}/api/snippets`;
      const params = [];
      if (selectedCategory) params.push(`category=${selectedCategory}`);
      if (snippetSearch) params.push(`search=${encodeURIComponent(snippetSearch)}`);
      if (params.length) url += '?' + params.join('&');
      
      const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setSnippets(response.data.snippets || []);
    } catch (e) {
      console.log('Snippets unavailable');
    } finally {
      setLoadingSnippets(false);
    }
  }, [selectedCategory, snippetSearch]);
  
  // Fetch suggestion history
  const fetchHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/autocomplete/history?limit=30`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuggestionHistory(response.data.history || []);
    } catch (e) {
      console.log('History unavailable');
    } finally {
      setLoadingHistory(false);
    }
  }, []);
  
  useEffect(() => {
    if (isOpen && blocks.length > 0) {
      setSelectedBlock(0);
      setEditedCode({});
      setHasChanges(false);
      setSuggestion('');
      fetchHistory();
      fetchSnippets();
    }
  }, [isOpen, fetchHistory, fetchSnippets]);
  
  useEffect(() => {
    if (showSnippets) fetchSnippets();
  }, [showSnippets, selectedCategory, fetchSnippets]);
  
  // Fetch AI autocomplete suggestion
  const fetchSuggestion = useCallback(async (code, language, cursorPosition) => {
    if (!aiEnabled || !code || code.length < 10) return;
    
    setLoadingSuggestion(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/autocomplete`, {
        code,
        language,
        cursor_position: cursorPosition
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.suggestion && response.data.confidence > 0.5) {
        setSuggestion(response.data.suggestion);
      } else {
        setSuggestion('');
      }
    } catch (e) {
      console.log('Autocomplete unavailable');
      setSuggestion('');
    } finally {
      setLoadingSuggestion(false);
    }
  }, [aiEnabled]);
  
  const handleCodeChange = useCallback((value) => {
    if (blocks[selectedBlock]) {
      const blockId = originalBlocks[selectedBlock].id;
      setEditedCode(prev => ({ ...prev, [blockId]: value }));
      setHasChanges(true);
      setSuggestion(''); // Clear suggestion on change
      
      // Debounce autocomplete request
      if (suggestionTimeoutRef.current) {
        clearTimeout(suggestionTimeoutRef.current);
      }
      
      if (aiEnabled && editorRef.current) {
        suggestionTimeoutRef.current = setTimeout(() => {
          const position = editorRef.current.getPosition();
          const model = editorRef.current.getModel();
          if (position && model) {
            const offset = model.getOffsetAt(position);
            const block = blocks[selectedBlock];
            fetchSuggestion(value, block?.language || block?.type || 'javascript', offset);
          }
        }, 800); // Wait 800ms after typing stops
      }
    }
  }, [selectedBlock, originalBlocks, blocks, aiEnabled, fetchSuggestion]);
  
  // Accept suggestion with Enter key
  const handleEditorMount = (editor, monaco) => {
    editorRef.current = editor;
    
    // Add Enter key handler for accepting suggestions
    editor.addCommand(monaco.KeyCode.Enter, () => {
      if (suggestion) {
        // Insert suggestion at cursor
        const position = editor.getPosition();
        editor.executeEdits('autocomplete', [{
          range: new monaco.Range(position.lineNumber, position.column, position.lineNumber, position.column),
          text: suggestion
        }]);
        setSuggestion('');
        toast.success('Sugerencia aplicada');
        fetchHistory(); // Refresh history
      } else {
        // Normal enter behavior
        editor.trigger('keyboard', 'type', { text: '\n' });
      }
    });
  };
  
  // Apply suggestion from history
  const applySuggestionFromHistory = async (item) => {
    if (editorRef.current) {
      const position = editorRef.current.getPosition();
      const monaco = window.monaco;
      if (position && monaco) {
        editorRef.current.executeEdits('history', [{
          range: new monaco.Range(position.lineNumber, position.column, position.lineNumber, position.column),
          text: item.suggestion
        }]);
        toast.success('Sugerencia del historial aplicada');
        setShowHistory(false);
        
        // Mark as accepted
        try {
          const token = localStorage.getItem('token');
          await axios.post(`${API}/api/autocomplete/history/${item.id}/accept`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          fetchHistory();
        } catch (e) { /* Silent fail for history accept */ }
      }
    }
  };
  
  // Delete suggestion from history
  const deleteSuggestion = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/autocomplete/history/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuggestionHistory(prev => prev.filter(s => s.id !== id));
      toast.success('Sugerencia eliminada');
    } catch (e) { /* Silent fail for delete */ }
  };
  
  // Save current code as snippet
  const saveAsSnippet = async () => {
    if (!newSnippetName.trim()) {
      toast.error('Ingresa un nombre para el snippet');
      return;
    }
    const currentBlock = blocks[selectedBlock];
    if (!currentBlock) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/snippets`, {
        name: newSnippetName,
        code: currentBlock.code,
        language: currentBlock.language || currentBlock.type,
        category: newSnippetCategory
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success('Snippet guardado');
      setShowSaveSnippet(false);
      setNewSnippetName('');
      fetchSnippets();
    } catch (e) {
      toast.error('Error al guardar snippet');
    }
  };
  
  // Save suggestion from history as snippet
  const saveHistoryAsSnippet = async (item) => {
    const name = prompt('Nombre del snippet:', item.context?.substring(0, 30) || 'Mi snippet');
    if (!name) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/snippets/from-history/${item.id}?name=${encodeURIComponent(name)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Snippet guardado desde historial');
      fetchSnippets();
    } catch (e) {
      toast.error('Error al guardar');
    }
  };
  
  // Apply snippet in editor
  const applySnippet = async (snippet) => {
    if (editorRef.current) {
      const position = editorRef.current.getPosition();
      const monaco = window.monaco;
      if (position && monaco) {
        editorRef.current.executeEdits('snippet', [{
          range: new monaco.Range(position.lineNumber, position.column, position.lineNumber, position.column),
          text: snippet.code
        }]);
        toast.success(`Snippet "${snippet.name}" aplicado`);
        setShowSnippets(false);
        
        // Increment use count
        try {
          const token = localStorage.getItem('token');
          await axios.post(`${API}/api/snippets/${snippet.id}/use`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } catch (e) { /* Silent fail for use count */ }
      }
    }
  };
  
  // Delete snippet
  const deleteSnippet = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/snippets/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSnippets(prev => prev.filter(s => s.id !== id));
      toast.success('Snippet eliminado');
    } catch (e) { /* Silent fail for delete */ }
  };
  
  const copyCode = () => {
    if (blocks[selectedBlock]) {
      navigator.clipboard.writeText(blocks[selectedBlock].code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Código copiado');
    }
  };
  
  const openInCodeSandbox = () => {
    const url = generateCodeSandboxUrl(originalBlocks, editedCode);
    window.open(url, '_blank');
  };
  
  const refreshPreview = () => setPreviewKey(prev => prev + 1);
  
  const resetCode = () => {
    setEditedCode({});
    setHasChanges(false);
    refreshPreview();
    toast.success('Código restaurado');
  };
  
  const applyChanges = () => {
    refreshPreview();
    toast.success('Preview actualizado');
  };
  
  if (!isOpen) return null;
  
  const previewHtml = generatePreviewHtml(previewable.length > 0 ? originalBlocks.filter(b => ['html', 'javascript', 'css'].includes(b.type)) : [], editedCode);
  const currentBlock = blocks[selectedBlock];
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className={`glass rounded-2xl overflow-hidden flex flex-col ${fullscreen ? 'w-full h-full' : 'w-full max-w-6xl max-h-[90vh]'}`}
          onClick={(e) => e.stopPropagation()}
          data-testid="code-preview-modal"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[#FF6B00]/20">
                <Play className="w-5 h-5 text-[#FF6B00]" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Preview en Vivo</h2>
                <p className="text-xs text-gray-500">{blocks.length} bloque(s) • {aiEnabled ? 'IA activa' : 'IA desactivada'}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setAiEnabled(!aiEnabled)}
                className={`flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs ${aiEnabled ? 'bg-purple-500/20 text-purple-400' : 'bg-white/5 text-gray-500'}`}
                title={aiEnabled ? 'Desactivar autocompletado IA' : 'Activar autocompletado IA'}
              >
                <Sparkles className="w-3 h-3" />
                <span className="hidden sm:inline">IA</span>
              </button>
              <button
                onClick={() => { setShowSnippets(!showSnippets); setShowHistory(false); }}
                className={`flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs ${showSnippets ? 'bg-green-500/20 text-green-400' : 'bg-white/5 text-gray-500'}`}
                title="Ver snippets favoritos"
                data-testid="snippets-btn"
              >
                <Bookmark className="w-3 h-3" />
                <span className="hidden sm:inline">Snippets</span>
                {snippets.length > 0 && <span className="text-[10px] ml-1">({snippets.length})</span>}
              </button>
              <button
                onClick={() => { setShowHistory(!showHistory); setShowSnippets(false); }}
                className={`flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs ${showHistory ? 'bg-amber-500/20 text-amber-400' : 'bg-white/5 text-gray-500'}`}
                title="Ver historial de sugerencias"
                data-testid="history-btn"
              >
                <RefreshCw className="w-3 h-3" />
                <span className="hidden sm:inline">Historial</span>
                {suggestionHistory.length > 0 && <span className="text-[10px] ml-1">({suggestionHistory.length})</span>}
              </button>
              {hasChanges && (
                <button onClick={resetCode} className="px-3 py-1.5 rounded-lg text-xs text-gray-400 hover:text-white hover:bg-white/10">
                  Restaurar
                </button>
              )}
              <button
                onClick={openInCodeSandbox}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 text-sm"
                data-testid="open-codesandbox-btn"
              >
                <ExternalLink className="w-4 h-4" />
                <span className="hidden sm:inline">CodeSandbox</span>
              </button>
              <button onClick={() => setFullscreen(!fullscreen)} className="p-2 rounded-lg hover:bg-white/10 text-gray-400">
                {fullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
              </button>
              <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 text-gray-400">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          {/* History Panel */}
          {showHistory && (
            <div className="border-b border-white/10 bg-amber-500/5 max-h-48 overflow-y-auto">
              <div className="p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-amber-400">Historial de Sugerencias</span>
                  {suggestionHistory.length > 0 && (
                    <button onClick={() => { setSuggestionHistory([]); }} className="text-[10px] text-gray-500 hover:text-red-400">
                      Limpiar
                    </button>
                  )}
                </div>
                {loadingHistory ? (
                  <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-amber-400" /></div>
                ) : suggestionHistory.length === 0 ? (
                  <p className="text-xs text-gray-500 text-center py-4">Sin sugerencias guardadas</p>
                ) : (
                  <div className="space-y-2">
                    {suggestionHistory.slice(0, 10).map((item) => (
                      <div key={item.id} className="flex items-start gap-2 p-2 rounded-lg bg-white/5 hover:bg-white/10 group">
                        <div className={`w-2 h-2 mt-1.5 rounded-full flex-shrink-0 ${
                          item.language === 'html' ? 'bg-orange-500' :
                          item.language === 'css' ? 'bg-blue-500' :
                          item.language === 'javascript' ? 'bg-yellow-500' : 'bg-gray-500'
                        }`} />
                        <div className="flex-1 min-w-0">
                          <code className="text-xs text-gray-300 block truncate">{item.suggestion}</code>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-[10px] text-gray-500">{item.language}</span>
                            {item.accepted && <span className="text-[10px] text-green-400">✓ usado</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
                          <button onClick={() => applySuggestionFromHistory(item)} className="p-1 rounded hover:bg-green-500/20 text-green-400" title="Usar">
                            <Check className="w-3 h-3" />
                          </button>
                          <button onClick={() => saveHistoryAsSnippet(item)} className="p-1 rounded hover:bg-yellow-500/20 text-yellow-400" title="Guardar como snippet">
                            <Star className="w-3 h-3" />
                          </button>
                          <button onClick={() => deleteSuggestion(item.id)} className="p-1 rounded hover:bg-red-500/20 text-red-400" title="Eliminar">
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Snippets Panel */}
          {showSnippets && (
            <div className="border-b border-white/10 bg-green-500/5 max-h-64 overflow-y-auto">
              <div className="p-3">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-medium text-green-400">Snippets Favoritos</span>
                  <button onClick={() => setShowSaveSnippet(true)} className="flex items-center gap-1 px-2 py-1 rounded bg-green-500/20 text-green-400 text-xs hover:bg-green-500/30">
                    <Plus className="w-3 h-3" />Guardar actual
                  </button>
                </div>
                
                {/* Search and filter */}
                <div className="flex gap-2 mb-3">
                  <div className="flex-1 relative">
                    <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-gray-500" />
                    <input
                      type="text"
                      placeholder="Buscar snippets..."
                      value={snippetSearch}
                      onChange={(e) => setSnippetSearch(e.target.value)}
                      className="w-full pl-7 pr-2 py-1.5 rounded bg-white/5 border border-white/10 text-xs text-white placeholder-gray-500"
                    />
                  </div>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="px-2 py-1.5 rounded bg-white/5 border border-white/10 text-xs text-gray-300"
                  >
                    <option value="">Todas</option>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                
                {/* Save snippet form */}
                {showSaveSnippet && (
                  <div className="mb-3 p-2 rounded bg-white/5 border border-green-500/30">
                    <div className="flex gap-2 mb-2">
                      <input
                        type="text"
                        placeholder="Nombre del snippet"
                        value={newSnippetName}
                        onChange={(e) => setNewSnippetName(e.target.value)}
                        className="flex-1 px-2 py-1.5 rounded bg-white/5 border border-white/10 text-xs text-white"
                      />
                      <select value={newSnippetCategory} onChange={(e) => setNewSnippetCategory(e.target.value)} className="px-2 py-1.5 rounded bg-white/5 border border-white/10 text-xs text-gray-300">
                        {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div className="flex justify-end gap-2">
                      <button onClick={() => setShowSaveSnippet(false)} className="px-2 py-1 rounded text-xs text-gray-400 hover:text-white">Cancelar</button>
                      <button onClick={saveAsSnippet} className="px-2 py-1 rounded bg-green-500/20 text-green-400 text-xs hover:bg-green-500/30">Guardar</button>
                    </div>
                  </div>
                )}
                
                {loadingSnippets ? (
                  <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-green-400" /></div>
                ) : snippets.length === 0 ? (
                  <p className="text-xs text-gray-500 text-center py-4">Sin snippets guardados</p>
                ) : (
                  <div className="space-y-2">
                    {snippets.map((snippet) => (
                      <div key={snippet.id} className="flex items-start gap-2 p-2 rounded-lg bg-white/5 hover:bg-white/10 group">
                        <div className={`w-2 h-2 mt-1.5 rounded-full flex-shrink-0 ${
                          snippet.language === 'html' ? 'bg-orange-500' :
                          snippet.language === 'css' ? 'bg-blue-500' :
                          snippet.language === 'javascript' ? 'bg-yellow-500' : 'bg-gray-500'
                        }`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-white">{snippet.name}</span>
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/10 text-gray-400">{snippet.category}</span>
                          </div>
                          <code className="text-[10px] text-gray-400 block truncate mt-0.5">{snippet.code.substring(0, 50)}...</code>
                          <span className="text-[10px] text-gray-600">Usado {snippet.use_count}x</span>
                        </div>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
                          <button onClick={() => applySnippet(snippet)} className="p-1 rounded hover:bg-green-500/20 text-green-400" title="Usar">
                            <Check className="w-3 h-3" />
                          </button>
                          <button onClick={() => deleteSnippet(snippet.id)} className="p-1 rounded hover:bg-red-500/20 text-red-400" title="Eliminar">
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Tabs */}
          <div className="flex border-b border-white/10">
            <button
              onClick={() => setActiveTab('preview')}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${activeTab === 'preview' ? 'text-[#FF6B00] border-b-2 border-[#FF6B00]' : 'text-gray-400 hover:text-white'}`}
            >
              <Eye className="w-4 h-4" />Preview
            </button>
            <button
              onClick={() => setActiveTab('editor')}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${activeTab === 'editor' ? 'text-[#FF6B00] border-b-2 border-[#FF6B00]' : 'text-gray-400 hover:text-white'}`}
            >
              <Edit3 className="w-4 h-4" />Editor
              {hasChanges && <span className="w-2 h-2 rounded-full bg-[#FF6B00]" />}
            </button>
            <button
              onClick={() => setActiveTab('code')}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${activeTab === 'code' ? 'text-[#FF6B00] border-b-2 border-[#FF6B00]' : 'text-gray-400 hover:text-white'}`}
            >
              <Code2 className="w-4 h-4" />Código ({blocks.length})
            </button>
          </div>
          
          {/* Content */}
          <div className="flex-1 overflow-hidden flex">
            {activeTab === 'preview' && (
              <div className="flex-1 flex flex-col">
                {hasReact ? (
                  <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-gray-900 to-black p-8">
                    <div className="text-center">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-blue-500/20 flex items-center justify-center">
                        <ExternalLink className="w-8 h-8 text-blue-400" />
                      </div>
                      <h3 className="text-xl font-bold text-white mb-2">Código React Detectado</h3>
                      <p className="text-gray-400 mb-6 max-w-md">Abre en CodeSandbox para ver el preview completo con compilación.</p>
                      <button onClick={openInCodeSandbox} className="px-6 py-3 rounded-xl bg-blue-500 text-white font-medium hover:bg-blue-600 flex items-center gap-2 mx-auto">
                        <ExternalLink className="w-5 h-5" />Abrir en CodeSandbox
                      </button>
                    </div>
                  </div>
                ) : previewable.length > 0 ? (
                  <>
                    <div className="p-2 bg-white/5 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <button onClick={refreshPreview} className="p-1.5 rounded hover:bg-white/10 text-gray-400" title="Refrescar">
                          <RefreshCw className="w-4 h-4" />
                        </button>
                        <span className="text-xs text-gray-500">Preview HTML/CSS/JS {hasChanges && '(editado)'}</span>
                      </div>
                      {hasChanges && (
                        <button onClick={applyChanges} className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-green-500/20 text-green-400 hover:bg-green-500/30">
                          <Play className="w-3 h-3" />Aplicar cambios
                        </button>
                      )}
                    </div>
                    <div className="flex-1 bg-white">
                      <iframe key={previewKey} ref={iframeRef} srcDoc={previewHtml} className="w-full h-full border-0" sandbox="allow-scripts allow-same-origin" title="Code Preview" data-testid="preview-iframe" />
                    </div>
                  </>
                ) : (
                  <div className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                      <Code2 className="w-12 h-12 mx-auto mb-4 text-gray-600" />
                      <p className="text-gray-500">No hay código previsualizable</p>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {activeTab === 'editor' && (
              <div className="flex-1 flex">
                {/* Block selector sidebar */}
                <div className="w-48 border-r border-white/10 overflow-y-auto bg-[#1e1e1e]">
                  <div className="p-2 text-xs text-gray-500 uppercase border-b border-white/10">Archivos</div>
                  {blocks.map((block, i) => (
                    <button
                      key={block.id}
                      onClick={() => setSelectedBlock(i)}
                      className={`w-full px-3 py-2 text-left border-b border-white/5 transition-colors ${selectedBlock === i ? 'bg-[#FF6B00]/20 text-white' : 'text-gray-400 hover:bg-white/5'}`}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${
                          block.type === 'html' ? 'bg-orange-500' :
                          block.type === 'css' ? 'bg-blue-500' :
                          block.type === 'javascript' ? 'bg-yellow-500' :
                          block.type === 'react' ? 'bg-cyan-500' : 'bg-gray-500'
                        }`} />
                        <span className="text-xs font-mono">{block.language || block.type}</span>
                        {editedCode[originalBlocks[i]?.id] && <span className="w-1.5 h-1.5 rounded-full bg-[#FF6B00]" />}
                      </div>
                    </button>
                  ))}
                </div>
                
                {/* Monaco Editor */}
                <div className="flex-1 flex flex-col">
                  <div className="p-2 bg-[#1e1e1e] flex items-center justify-between border-b border-white/10">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400 font-mono">{currentBlock?.language || currentBlock?.type}</span>
                      {loadingSuggestion && <Loader2 className="w-3 h-3 animate-spin text-purple-400" />}
                      {aiEnabled && <span className="text-[10px] text-purple-400/60">IA activa</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={copyCode} className="flex items-center gap-1 px-2 py-1 rounded text-xs text-gray-400 hover:text-white hover:bg-white/10">
                        {copied ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
                        {copied ? 'Copiado' : 'Copiar'}
                      </button>
                    </div>
                  </div>
                  
                  {/* AI Suggestion banner */}
                  {suggestion && (
                    <div className="px-3 py-2 bg-purple-500/10 border-b border-purple-500/20 flex items-center justify-between">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <Sparkles className="w-4 h-4 text-purple-400 flex-shrink-0" />
                        <code className="text-xs text-purple-300 truncate">{suggestion.substring(0, 60)}...</code>
                      </div>
                      <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                        <span className="text-[10px] text-purple-400/60">Enter para aceptar</span>
                        <button onClick={() => setSuggestion('')} className="p-1 rounded hover:bg-white/10 text-gray-500">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex-1">
                    <Editor
                      height="100%"
                      language={getMonacoLanguage(currentBlock?.type, currentBlock?.language)}
                      value={currentBlock?.code || ''}
                      onChange={handleCodeChange}
                      onMount={handleEditorMount}
                      theme="vs-dark"
                      options={{
                        minimap: { enabled: false },
                        fontSize: 14,
                        lineNumbers: 'on',
                        scrollBeyondLastLine: false,
                        automaticLayout: true,
                        tabSize: 2,
                        wordWrap: 'on'
                      }}
                      data-testid="monaco-editor"
                    />
                  </div>
                </div>
              </div>
            )}
            
            {activeTab === 'code' && (
              <div className="flex-1 flex">
                <div className="w-48 border-r border-white/10 overflow-y-auto">
                  {blocks.map((block, i) => (
                    <button
                      key={block.id}
                      onClick={() => setSelectedBlock(i)}
                      className={`w-full px-4 py-3 text-left border-b border-white/5 transition-colors ${selectedBlock === i ? 'bg-[#FF6B00]/20 text-white' : 'text-gray-400 hover:bg-white/5'}`}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${
                          block.type === 'html' ? 'bg-orange-500' :
                          block.type === 'css' ? 'bg-blue-500' :
                          block.type === 'javascript' ? 'bg-yellow-500' :
                          block.type === 'react' ? 'bg-cyan-500' : 'bg-gray-500'
                        }`} />
                        <span className="text-xs uppercase font-mono">{block.language || block.type}</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1 truncate">{block.code.substring(0, 30)}...</p>
                    </button>
                  ))}
                </div>
                <div className="flex-1 flex flex-col">
                  <div className="p-2 bg-white/5 flex items-center justify-between">
                    <span className="text-xs text-gray-500 font-mono">{currentBlock?.language || currentBlock?.type}</span>
                    <button onClick={copyCode} className="flex items-center gap-1 px-2 py-1 rounded text-xs text-gray-400 hover:text-white hover:bg-white/10">
                      {copied ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
                      {copied ? 'Copiado' : 'Copiar'}
                    </button>
                  </div>
                  <pre className="flex-1 p-4 overflow-auto text-sm text-gray-300 font-mono bg-black/30">
                    <code>{currentBlock?.code || 'Sin código'}</code>
                  </pre>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default CodePreviewModal;
